import React, { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { URLS } from "../Url";
import { Phone, Mail, MapPin, ArrowRight, ArrowLeft, Sparkles, MessageCircle } from "lucide-react";

const EVENT_TYPES = [
  "Birthday Celebration",
  "Love Proposal",
  "Marriage Proposal",
  "Anniversary",
  "Bride To Be",
  "Groom To Be",
  "Birthday Surprise",
  "Mom To Be",
  "Dad To Be",
  "Farewell Party",
  "Baby Shower",
  "Kitty Party",
];

export default function EnquiryForm({ Contact }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobileNumber: "",
    description: "",
    eventName: "",
  });
  const [formStep, setFormStep] = useState(0);

  const handleChange = useCallback((e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      axios
        .post(URLS.AddEnquiry, form)
        .then((r) => {
          if (r.status === 200) {
            toast.success(r.data.message);
            setForm({
              name: "",
              email: "",
              mobileNumber: "",
              description: "",
              eventName: "",
            });
            setFormStep(0);
          }
        })
        .catch((err) => {
          if (err.response?.status === 400) {
            toast.error(err.response.data.message);
          }
        });
    },
    [form]
  );

  const contactItems = [
    {
      icon: <Phone size={14} />,
      label: "Call Us",
      val: "+91 8341428342",
      href: "tel:8341428342",
    },
    {
      icon: <MessageCircle size={14} />,
      label: "WhatsApp",
      val: "+91 8341428342",
      href: "https://wa.me/918341428342",
    },
    {
      icon: <Mail size={14} />,
      label: "Email",
      val: Contact?.email || "carnivalcastle02@gmail.com",
      href: `mailto:${Contact?.email || "carnivalcastle02@gmail.com"}`,
    },
    {
      icon: <MapPin size={14} />,
      label: "Location",
      val: Contact?.address || "Hyderabad, Telangana",
      href: null,
    },
  ];

  return (
    <section className="cc-sec cc-sec-beige" id="enquiry">
      <div className="home-enq-wrapper">
        <div className="text-center rv" style={{ marginBottom: 32 }}>
          <span className="cc-tag">✦ Get In Touch ✦</span>
          <h2 className="cc-h">
            Plan Your <span>Perfect Event</span>
          </h2>
          <p className="cc-sub">
            Fill in the form and our team will respond within 2 hours with a personalised plan.
          </p>
        </div>

        <div className="home-enq-card rv">
          {/* Left: Concierge Information Panel */}
          <div className="home-enq-left">
            <div className="home-enq-left-top">
              <span className="home-enq-eyebrow">✦ CARNIVAL CASTLE CONCIERGE ✦</span>
              <h3 className="home-enq-left-title">Let's Create Magic Together</h3>
              <p className="home-enq-left-desc">
                Share your celebration details and our team will craft an unforgettable private theatre experience for you.
              </p>

              <div className="home-enq-progress-box">
                <div className="home-enq-progress-head">
                  <span className="home-enq-progress-label">
                    Step {formStep + 1} of 3 · {["Your Details", "Choose Occasion", "Your Message"][formStep]}
                  </span>
                </div>
                <div className="home-enq-step-bar">
                  {[0, 1, 2].map((s) => (
                    <div
                      key={s}
                      className={`home-enq-step-pill ${formStep >= s ? "active" : ""}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="home-enq-left-bottom">
              <div className="home-enq-divider" />
              <div className="home-enq-contact-grid">
                {contactItems.map((it, i) => (
                  <div key={i} className="home-enq-contact-item">
                    <div className="home-enq-ci-icon-box">{it.icon}</div>
                    <div className="home-enq-ci-text">
                      <span className="home-enq-ci-label">{it.label}</span>
                      {it.href ? (
                        <a
                          href={it.href}
                          className="home-enq-ci-link"
                          target={it.href.startsWith("http") ? "_blank" : undefined}
                          rel="noopener noreferrer"
                        >
                          {it.val}
                        </a>
                      ) : (
                        <span className="home-enq-ci-val">{it.val}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Concierge Form Panel */}
          <div className="home-enq-right">
            <div className="home-enq-right-inner">
              <h3 className="home-enq-form-title">
                We'd Love To Hear <span>From You!</span>
              </h3>
              <p className="home-enq-form-subtitle">
                Your celebration, your way — tell us what you have in mind.
              </p>

              <form onSubmit={handleSubmit} className="home-enq-form">
                {formStep === 0 && (
                  <div className="home-enq-step-content">
                    <div className="home-enq-fields-grid">
                      <div className="home-enq-field">
                        <label className="home-enq-field-label">Your Name</label>
                        <input
                          className="home-enq-input"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          placeholder="e.g. Priya Sharma"
                          required
                        />
                      </div>
                      <div className="home-enq-field">
                        <label className="home-enq-field-label">Phone Number</label>
                        <input
                          className="home-enq-input"
                          name="mobileNumber"
                          value={form.mobileNumber}
                          onChange={handleChange}
                          placeholder="+91 00000 00000"
                          required
                        />
                      </div>
                    </div>

                    <div className="home-enq-field" style={{ marginTop: 12 }}>
                      <label className="home-enq-field-label">Email Address</label>
                      <input
                        className="home-enq-input"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@email.com"
                        required
                      />
                    </div>

                    <div className="home-enq-actions">
                      <button
                        type="button"
                        className="home-enq-btn-primary"
                        onClick={() => {
                          if (form.name && form.mobileNumber && form.email) {
                            setFormStep(1);
                          } else {
                            toast.warn("Please fill all fields.");
                          }
                        }}
                      >
                        Next: Choose Occasion <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {formStep === 1 && (
                  <div className="home-enq-step-content">
                    <label className="home-enq-field-label" style={{ marginBottom: 10 }}>
                      Select Your Occasion
                    </label>
                    <div className="home-enq-occ-grid">
                      {EVENT_TYPES.map((ev, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`home-enq-occ-chip ${form.eventName === ev ? "selected" : ""}`}
                          onClick={() => setForm((f) => ({ ...f, eventName: ev }))}
                        >
                          {ev}
                        </button>
                      ))}
                    </div>

                    <div className="home-enq-actions-row">
                      <button
                        type="button"
                        className="home-enq-btn-secondary"
                        onClick={() => setFormStep(0)}
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button
                        type="button"
                        className="home-enq-btn-primary"
                        onClick={() => {
                          if (form.eventName) {
                            setFormStep(2);
                          } else {
                            toast.warn("Please select an occasion.");
                          }
                        }}
                      >
                        Next: Your Message <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {formStep === 2 && (
                  <div className="home-enq-step-content">
                    <div className="home-enq-field">
                      <label className="home-enq-field-label">Tell Us About Your Event</label>
                      <textarea
                        className="home-enq-input home-enq-textarea"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={4}
                        placeholder={`Tell us about your ${form.eventName || "event"} — preferred date, guests, or special wishes...`}
                      />
                    </div>

                    <div className="home-enq-summary-card">
                      <div className="home-enq-summary-label">Summary</div>
                      <div className="home-enq-summary-text">
                        <span className="home-enq-summary-strong">{form.name}</span> · {form.mobileNumber} · {form.email}
                        <div className="home-enq-summary-badge">
                          🎉 {form.eventName || "Celebration"}
                        </div>
                      </div>
                    </div>

                    <div className="home-enq-actions-row">
                      <button
                        type="button"
                        className="home-enq-btn-secondary"
                        onClick={() => setFormStep(1)}
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button type="submit" className="home-enq-btn-primary">
                        <Sparkles size={15} /> Send My Request
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .home-enq-wrapper {
          max-width: 1220px;
          margin: 0 auto;
          padding: 0 24px;
          width: 100%;
          box-sizing: border-box;
        }

        .home-enq-card {
          background-color: #FFFFFF;
          border: 1px solid #EBE4D8;
          border-radius: 16px;
          box-shadow: 0 12px 32px rgba(26, 0, 48, 0.04);
          overflow: hidden;
          display: flex;
          align-items: stretch;
          width: 100%;
        }

        /* ── Left Concierge Panel (52%) ── */
        .home-enq-left {
          flex: 0 0 52%;
          width: 52%;
          background-color: #1E0038;
          padding: 34px 34px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          color: #FFFFFF;
          border-right: 1px solid rgba(235, 228, 216, 0.08);
          box-sizing: border-box;
        }

        .home-enq-eyebrow {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #C9A84C;
          margin-bottom: 8px;
        }

        .home-enq-left-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 24px;
          font-weight: 700;
          color: #FFFFFF;
          margin: 0 0 8px 0;
          line-height: 1.25;
        }

        .home-enq-left-desc {
          font-size: 12.5px;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.72);
          margin: 0 0 18px 0;
        }

        .home-enq-progress-box {
          margin-bottom: 4px;
        }

        .home-enq-progress-head {
          margin-bottom: 7px;
        }

        .home-enq-progress-label {
          font-size: 10px;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: #E8D5A0;
          font-weight: 600;
        }

        .home-enq-step-bar {
          display: flex;
          gap: 6px;
        }

        .home-enq-step-pill {
          flex: 1;
          height: 3px;
          border-radius: 2px;
          background-color: rgba(255, 255, 255, 0.15);
          transition: background-color 0.25s ease;
        }

        .home-enq-step-pill.active {
          background-color: #C9A84C;
        }

        .home-enq-divider {
          height: 1px;
          background-color: rgba(235, 228, 216, 0.12);
          margin: 18px 0 16px 0;
        }

        .home-enq-contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px 18px;
        }

        .home-enq-contact-item {
          display: flex;
          align-items: center;
          gap: 9px;
          min-width: 0;
        }

        .home-enq-ci-icon-box {
          width: 26px;
          height: 26px;
          border-radius: 6px;
          background-color: rgba(201, 168, 76, 0.12);
          color: #C9A84C;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .home-enq-ci-text {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .home-enq-ci-label {
          font-size: 9.5px;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.45);
          font-weight: 600;
        }

        .home-enq-ci-link,
        .home-enq-ci-val {
          font-size: 11.5px;
          color: rgba(255, 255, 255, 0.88);
          font-weight: 500;
          text-decoration: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .home-enq-ci-link:hover {
          color: #C9A84C;
        }

        /* ── Right Form Panel (48%) ── */
        .home-enq-right {
          flex: 0 0 48%;
          width: 48%;
          background-color: #FFFFFF;
          padding: 34px 36px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          box-sizing: border-box;
          min-width: 0;
        }

        .home-enq-right-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
          justify-content: space-between;
        }

        .home-enq-form-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 22px;
          font-weight: 700;
          color: #1A0030;
          margin: 0 0 4px 0;
        }

        .home-enq-form-title span {
          color: #5E0E73;
        }

        .home-enq-form-subtitle {
          font-size: 12.5px;
          color: #7A7082;
          margin: 0 0 18px 0;
        }

        .home-enq-fields-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .home-enq-field-label {
          display: block;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #4A3E54;
          margin-bottom: 5px;
        }

        .home-enq-input {
          width: 100%;
          background-color: #FAFAF8;
          border: 1px solid #E2D9CC;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 13.5px;
          color: #1A0030;
          font-family: inherit;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }

        .home-enq-input:focus {
          background-color: #FFFFFF;
          border-color: #5E0E73;
          box-shadow: 0 0 0 3px rgba(94, 14, 115, 0.08);
        }

        .home-enq-input::placeholder {
          color: #A39BA8;
          font-size: 13px;
        }

        .home-enq-textarea {
          resize: vertical;
          min-height: 85px;
          line-height: 1.45;
        }

        .home-enq-actions {
          margin-top: 18px;
        }

        .home-enq-actions-row {
          margin-top: 16px;
          display: flex;
          gap: 10px;
        }

        .home-enq-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 44px;
          background-color: #5E0E73;
          color: #FFFFFF;
          border: 1px solid #C9A84C;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.4px;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.2s ease;
        }

        .home-enq-btn-primary:hover {
          background-color: #4A0B5B;
          color: #FFFFFF;
          transform: translateY(-1px);
        }

        .home-enq-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          flex: 0 0 110px;
          height: 44px;
          background-color: transparent;
          color: #5E0E73;
          border: 1px solid #D6CCE0;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }

        .home-enq-btn-secondary:hover {
          background-color: #F8F5F0;
          border-color: #5E0E73;
        }

        .home-enq-occ-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 7px;
          margin-bottom: 6px;
        }

        .home-enq-occ-chip {
          padding: 8px 6px;
          border-radius: 6px;
          border: 1px solid #E2D9CC;
          background-color: #FAFAF8;
          font-size: 11.5px;
          font-weight: 500;
          color: #2D143E;
          cursor: pointer;
          text-align: center;
          transition: all 0.18s ease;
          line-height: 1.25;
        }

        .home-enq-occ-chip:hover {
          border-color: #C9A84C;
          background-color: #FFFFFF;
        }

        .home-enq-occ-chip.selected {
          background-color: #5E0E73;
          border-color: #C9A84C;
          color: #FFFFFF;
          font-weight: 600;
        }

        .home-enq-summary-card {
          background-color: #F8F5F0;
          border: 1px solid #EBE4D8;
          border-radius: 8px;
          padding: 12px 14px;
          margin-top: 14px;
        }

        .home-enq-summary-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #B89535;
          margin-bottom: 4px;
        }

        .home-enq-summary-text {
          font-size: 12.5px;
          color: #4A3E54;
          line-height: 1.4;
        }

        .home-enq-summary-strong {
          color: #1A0030;
          font-weight: 600;
        }

        .home-enq-summary-badge {
          margin-top: 4px;
          color: #5E0E73;
          font-weight: 600;
          font-size: 12px;
        }

        /* ── Responsive ── */
        @media (max-width: 991px) {
          .home-enq-card {
            flex-direction: column;
          }
          .home-enq-left,
          .home-enq-right {
            flex: 1 1 100%;
            width: 100%;
          }
          .home-enq-left {
            border-right: none;
            border-bottom: 1px solid rgba(235, 228, 216, 0.12);
            padding: 28px 22px;
          }
          .home-enq-right {
            padding: 28px 22px;
          }
          .home-enq-occ-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 640px) {
          .home-enq-wrapper {
            padding: 0 16px;
          }
          .home-enq-left {
            padding: 22px 16px;
          }
          .home-enq-right {
            padding: 22px 16px;
          }
          .home-enq-contact-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }
          .home-enq-fields-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
        }
      `}</style>
    </section>
  );
}